import { AuthService } from '@application/authentication/auth.service';
import { Injectable, Scopes } from '@visiba/instantiation';

/**
 * Service that tracks the number of unhandled issues that the user needs to
 * take action on.
 */
@Injectable({
	scope: Scopes.Singleton,
})
export class UnhandledIssuesService {
	public issues: Issue[] = [];
	private hasScheduled = false;
	private callbacks: Callback[] = [];

	// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
	constructor(private authService: AuthService) {
		authService.on('onAuthenticationTermination', () => {
			this.removeAll();
		});
	}

	/**
	 * Registers a new issue.
	 */
	public addIssue(trackedBy: string, groupedBy?: string[]): void {
		this.issues.push(new Issue(trackedBy, groupedBy ?? []));

		this.scheduleUpdate();
	}

	/**
	 * Returns a count of issues, either all or grouped by the given grouping tags.
	 */
	public count(filterByTags?: string[], issues = this.issues): number {
		if (filterByTags == null || filterByTags.length === 0) return issues.length;

		return issues.filter((issue) => {
			return issue.tags.some((tag) => filterByTags.includes(tag));
		}).length;
	}

	/**
	 * Returns a count of issues from a tracking id, either all or grouped by the given grouping tags.
	 */
	public getCountByIssuesById(tackingId: string, filterByTags?: string[]): number {
		const issues = this.issues.filter((issue) => issue.trackingId === tackingId);

		return this.count(filterByTags, issues);
	}

	/**
	 * Invalidates issues matching the given tracking id, removing them to be
	 * counted.
	 */
	public removeIssuesByTrackingId(trackingId: string): void;
	public removeIssuesByTrackingId(...trackingIds: string[]): void;
	public removeIssuesByTrackingId(...args: string[]): void {
		const lengthBefore = this.callbacks.length;

		const trackingIds = Array.isArray(args) ? args : [args];
		for (const trackingId of trackingIds) {
			this.issues = this.issues.filter((issue) => issue.trackingId !== trackingId);
		}

		if (this.issues.length === lengthBefore) return;

		this.scheduleUpdate();
	}

	/**
	 * Issues are not deeply tracked. And multiple similar instances could exist
	 * simultaneously. So removing one "arbitrary" by matching tags is equal to
	 * finding a specific one.
	 */
	public removeArbitraryIssue(trackingId: string, mustBeTagAs: string): void;
	public removeArbitraryIssue(trackingId: string, mustBeTagAs: string[]): void;
	public removeArbitraryIssue(trackingId: string, _mustBeTagAs: string | string[]): void {
		const mustBeTagAs = (Array.isArray(_mustBeTagAs) ? _mustBeTagAs : [_mustBeTagAs]) as string[];

		const lengthBefore = this.issues.length;

		const foundMatch = this.issues.findIndex((issue) => {
			if (issue.trackingId !== trackingId) return false;

			let matches = 0;
			for (const tag of issue.tags) {
				if (mustBeTagAs.includes(tag)) {
					matches++;
				}
			}

			return matches >= mustBeTagAs.length;
		});

		if (foundMatch === -1) return;

		this.issues.splice(foundMatch, 1);

		if (this.issues.length === lengthBefore) return;

		this.scheduleUpdate();
	}

	public removeIssuesByTag(trackingId: string, mustBeTagAs: string): void;
	public removeIssuesByTag(trackingId: string, mustBeTagAs: string[]): void;
	public removeIssuesByTag(trackingId: string, _mustBeTagAs: string | string[]): void {
		const mustBeTagAs = (Array.isArray(_mustBeTagAs) ? _mustBeTagAs : [_mustBeTagAs]) as string[];

		const lengthBefore = this.issues.length;
		this.issues = this.issues.filter((issue) => {
			if (issue.trackingId !== trackingId) return true;

			return !issue.tags.some((tag) => mustBeTagAs.includes(tag));
		});

		if (this.issues.length === lengthBefore) return;

		this.scheduleUpdate();
	}

	public removeAll() {
		this.issues = [];
		this.scheduleUpdate();
	}

	/**
	 * Registers a callback to be called when issues are being added or invalidated.
	 */
	public onChange(callback: Callback): Disposer {
		this.callbacks.push(callback);

		return () => {
			const index = this.callbacks.findIndex((cb) => cb === callback);
			this.callbacks.splice(index, 1);
		};
	}

	private scheduleUpdate(): void {
		if (this.hasScheduled) return;

		this.hasScheduled = true;

		queueMicrotask(() => {
			for (const callback of this.callbacks) {
				callback();
			}

			this.hasScheduled = false;
		});
	}
}

class Issue {
	public readonly trackingId: string;
	public readonly tags: Readonly<string>[] = [];

	constructor(trackedBy: string, groupedBy: string[]) {
		this.trackingId = trackedBy;
		this.tags = groupedBy;
	}
}

export type Callback = () => void;
export type Disposer = () => void;
